<template>
  <div class="document-file-manager">
    <!-- File Upload Area -->
    <q-card flat bordered class="q-mb-md">
      <q-card-section>
        <div class="text-subtitle1 q-mb-md">
          <q-icon name="cloud_upload" class="q-mr-sm" />
          Upload Files
        </div>

        <q-file
          v-model="fileToUpload"
          label="Choose file to upload"
          outlined
          accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
          max-file-size="51200000"
          @update:model-value="handleFileSelected"
        >
          <template #prepend>
            <q-icon name="attach_file" />
          </template>
        </q-file>

        <!-- Title Input - REQUIRED -->
        <q-input
          v-if="fileToUpload"
          v-model="fileTitle"
          label="Title *"
          outlined
          class="q-mt-md"
          :rules="[val => !!val || 'Title is required']"
          hint="Short descriptive title (e.g., 'VFD Datasheet', 'Wiring Diagram')"
          autofocus
        />

        <!-- Description Input - OPTIONAL -->
        <q-input
          v-if="fileToUpload"
          v-model="fileDescription"
          label="Description (optional)"
          type="textarea"
          rows="2"
          outlined
          class="q-mt-md"
          hint="Additional details or notes"
        />

        <q-btn
          v-if="fileToUpload"
          color="primary"
          label="Upload"
          icon="cloud_upload"
          class="q-mt-md"
          :loading="uploading"
          @click="uploadFile"
        />
      </q-card-section>
    </q-card>

    <!-- File List -->
    <q-card flat bordered>
      <q-card-section>
        <div class="text-subtitle1 q-mb-md">
          <q-icon name="folder_open" class="q-mr-sm" />
          Attached Files ({{ files.length }})
        </div>

        <q-list v-if="files.length > 0" separator>
          <q-item
            v-for="file in files"
            :key="file.id"
          >
            <q-item-section avatar>
              <q-avatar :color="getFileIconColor(file.file_extension)" text-color="white">
                <q-icon :name="getFileIcon(file.file_extension)" />
              </q-avatar>
            </q-item-section>

            <q-item-section>
              <!-- PRIMARY: User-entered title (prominent) -->
              <q-item-label class="text-weight-medium">
                {{ file.title || file.file_name }}
              </q-item-label>

              <!-- SECONDARY: Description (if provided) -->
              <q-item-label v-if="file.description" caption class="q-mt-xs">
                {{ file.description }}
              </q-item-label>

              <!-- TERTIARY: Technical details (filename, size, type) -->
              <q-item-label caption class="text-grey-6 q-mt-xs">
                {{ file.file_name }} • {{ file.formatted_file_size }} •
                {{ file.file_type === 'registry_shared' ? 'Registry Shared' : 'Instance Unique' }}
              </q-item-label>
            </q-item-section>

            <q-item-section side>
              <div class="row q-gutter-sm">
                <!-- Edit Description -->
                <q-btn
                  flat
                  round
                  dense
                  icon="edit"
                  color="primary"
                  @click="editFileDescription(file)"
                >
                  <q-tooltip>Edit description</q-tooltip>
                </q-btn>

                <!-- Download -->
                <q-btn
                  flat
                  round
                  dense
                  icon="download"
                  color="positive"
                  @click="downloadFile(file)"
                >
                  <q-tooltip>Download file</q-tooltip>
                </q-btn>

                <!-- Delete -->
                <q-btn
                  flat
                  round
                  dense
                  icon="delete"
                  color="negative"
                  @click="confirmDelete(file)"
                >
                  <q-tooltip>Delete file</q-tooltip>
                </q-btn>
              </div>
            </q-item-section>
          </q-item>
        </q-list>

        <div v-else class="text-center text-grey-6 q-pa-md">
          No files attached yet
        </div>
      </q-card-section>
    </q-card>

    <!-- Edit File Metadata Dialog -->
    <q-dialog v-model="editDialogOpen" persistent>
      <q-card style="min-width: 500px">
        <q-card-section class="bg-primary text-white">
          <div class="text-h6">Edit File Metadata</div>
        </q-card-section>

        <q-card-section>
          <!-- Title (required) -->
          <q-input
            v-model="editingTitle"
            label="Title *"
            outlined
            :rules="[val => !!val || 'Title is required']"
            class="q-mb-md"
            autofocus
          />

          <!-- Description (optional) -->
          <q-input
            v-model="editingDescription"
            label="Description (optional)"
            type="textarea"
            rows="3"
            outlined
            class="q-mb-md"
          />

          <!-- Filename (read-only) -->
          <q-input
            :model-value="editingFile?.file_name"
            label="Filename"
            outlined
            readonly
          />
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Cancel" @click="closeEditDialog" />
          <q-btn
            color="primary"
            label="Save"
            :loading="saving"
            @click="saveMetadata"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script setup>
  import { ref, onMounted, watch } from 'vue';
  import { api } from '@/services/api';
  import { useQuasar } from 'quasar';

  const $q = useQuasar();

  const props = defineProps({
    documentableType: {
      type: String,
      required: true,
    },
    documentableId: {
      type: Number,
      required: true,
    },
    fileType: {
      type: String,
      required: true,
      validator: (value) => ['registry_shared', 'instance_unique'].includes(value),
    },
    affectedDevicesCount: {
      type: Number,
      default: 0,
    },
  });

  const emit = defineEmits(['file-uploaded', 'file-deleted', 'file-updated']);

  const files = ref([]);
  const fileToUpload = ref(null);
  const fileTitle = ref('');
  const fileDescription = ref('');
  const uploading = ref(false);
  const editDialogOpen = ref(false);
  const editingFile = ref(null);
  const editingTitle = ref('');
  const editingDescription = ref('');
  const saving = ref(false);

  const loadFiles = async () => {
    try {
      const response = await api.get('/documentation-files', {
        documentable_type: props.documentableType,
        documentable_id: props.documentableId,
        file_type: props.fileType,
      });

      // API service auto-unwraps, so response IS the data array
      if (Array.isArray(response)) {
        files.value = response;
      } else if (response && response.data) {
        // Fallback for non-unwrapped responses
        files.value = response.data;
      } else {
        files.value = [];
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to load files',
      });
    }
  };

  const handleFileSelected = (file) => {
    if (file && file.size > 51200000) {
      $q.notify({
        type: 'warning',
        message: 'File size exceeds 50MB limit',
      });
      fileToUpload.value = null;
    }
  };

  const uploadFile = async () => {
    if (!fileToUpload.value) {return;}

    if (!fileTitle.value?.trim()) {
      $q.notify({
        type: 'warning',
        message: 'Please enter a title for the file',
      });
      return;
    }

    uploading.value = true;

    try {
      const formData = new FormData();
      formData.append('file', fileToUpload.value);
      formData.append('documentable_type', props.documentableType);
      formData.append('documentable_id', props.documentableId);
      formData.append('file_type', props.fileType);
      formData.append('title', fileTitle.value.trim());
      if (fileDescription.value) {
        formData.append('description', fileDescription.value);
      }

      // API helper automatically detects FormData and sets correct headers
      const response = await api.post('/documentation-files', formData);

      if (response.success) {
        $q.notify({
          type: 'positive',
          message: 'File uploaded successfully',
        });

        // Reset form
        fileToUpload.value = null;
        fileTitle.value = '';
        fileDescription.value = '';

        // Reload files
        await loadFiles();

        emit('file-uploaded', response.data);
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: error.message || 'Failed to upload file',
      });
    } finally {
      uploading.value = false;
    }
  };

  const downloadFile = async (file) => {
    try {
      const response = await api.get(`/documentation-files/${file.id}/download`, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.file_name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to download file',
      });
    }
  };

  const editFileDescription = (file) => {
    editingFile.value = file;
    editingTitle.value = file.title || '';
    editingDescription.value = file.description || '';
    editDialogOpen.value = true;
  };

  const closeEditDialog = () => {
    editDialogOpen.value = false;
    editingFile.value = null;
    editingTitle.value = '';
    editingDescription.value = '';
  };

  const saveMetadata = async () => {
    if (!editingFile.value) {return;}

    if (!editingTitle.value?.trim()) {
      $q.notify({
        type: 'warning',
        message: 'Title is required',
      });
      return;
    }

    saving.value = true;

    try {
      const response = await api.put(`/documentation-files/${editingFile.value.id}`, {
        title: editingTitle.value.trim(),
        description: editingDescription.value,
      });

      if (response.success) {
        $q.notify({
          type: 'positive',
          message: 'File metadata updated successfully',
        });

        await loadFiles();
        emit('file-updated', response.data);
        closeEditDialog();
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to update file metadata',
      });
    } finally {
      saving.value = false;
    }
  };

  const confirmDelete = (file) => {
    let message = `Are you sure you want to delete "${file.title || file.file_name}"?`;

    // Add warning for registry files
    if (file.file_type === 'registry_shared') {
      message += '\n\n⚠️ WARNING: This file is shared from the Device Part Registry. Deleting it will remove it from ALL devices using this part.';

      if (props.affectedDevicesCount > 0) {
        message += `\n\nThis will affect ${props.affectedDevicesCount} device(s).`;
      }
    }

    $q.dialog({
      title: 'Confirm Delete',
      message: message,
      persistent: true,
      ok: {
        label: 'Delete',
        color: 'negative',
        flat: false,
      },
      cancel: {
        label: 'Cancel',
        color: 'grey',
        flat: true,
      },
    }).onOk(async () => {
      await deleteFile(file);
    });
  };

  const deleteFile = async (file) => {
    try {
      const response = await api.delete(`/documentation-files/${file.id}`);

      if (response.success) {
        $q.notify({
          type: 'positive',
          message: 'File deleted successfully',
        });

        await loadFiles();
        emit('file-deleted', file.id);
      }
    } catch (error) {
      $q.notify({
        type: 'negative',
        message: 'Failed to delete file',
      });
    }
  };

  const getFileIcon = (extension) => {
    const ext = extension?.toLowerCase();
    if (['pdf'].includes(ext)) {return 'picture_as_pdf';}
    if (['doc', 'docx'].includes(ext)) {return 'description';}
    if (['xls', 'xlsx'].includes(ext)) {return 'table_chart';}
    if (['png', 'jpg', 'jpeg', 'gif'].includes(ext)) {return 'image';}
    return 'insert_drive_file';
  };

  const getFileIconColor = (extension) => {
    const ext = extension?.toLowerCase();
    if (['pdf'].includes(ext)) {return 'red';}
    if (['doc', 'docx'].includes(ext)) {return 'blue';}
    if (['xls', 'xlsx'].includes(ext)) {return 'green';}
    if (['png', 'jpg', 'jpeg', 'gif'].includes(ext)) {return 'purple';}
    return 'grey';
  };

  const formatDate = (dateString) => {
    if (!dateString) {return 'Unknown';}
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  onMounted(() => {
    loadFiles();
  });

  watch(() => [props.documentableType, props.documentableId, props.fileType], () => {
    loadFiles();
  }, { deep: true });
</script>

<style scoped>
.document-file-manager {
  width: 100%;
}
</style>
